import * as React from 'react';
import { cn } from '../../utils/cn';
import type { TextIconProps } from './text-icon.types';

/**
 * TextIcon Component
 *
 * Text with optional leading and trailing icons.
 * Perfect for labels, list items, and informational displays.
 */
export const TextIcon = React.forwardRef<HTMLDivElement, TextIconProps>(
    (
        {
            rightIcon,
            leftIcon,
            text,
            children,
            gap = 8,
            alignText = 'left',
            numberOfLines = 2,
            fontSize = 'medium',
            fontWeight = 'normal',
            fontColor,
            containerClassName,
            textClassName,
            testID,
            leftIconTestId,
            rightIconTestId,
            textTestId,
        },
        ref
    ) => {
        const fontSizeClasses = {
            xsmall: 'text-xs',
            small: 'text-sm',
            medium: 'text-base',
            large: 'text-lg',
            xlarge: 'text-xl',
            xxlarge: 'text-2xl',
        };

        const fontWeightClasses = {
            normal: 'font-normal',
            medium: 'font-medium',
            semibold: 'font-semibold',
            bold: 'font-bold',
        };

        const alignClasses = {
            auto: 'text-left',
            left: 'text-left',
            right: 'text-right',
            center: 'text-center',
            justify: 'text-justify',
        };

        const getLineClampClass = () => {
            if (numberOfLines === 1) return 'truncate';
            if (numberOfLines > 1) return `line-clamp-${numberOfLines}`;
            return '';
        };

        const lineClampClass = getLineClampClass();

        return (
            <div
                ref={ref}
                data-testid={testID || 'text-icon'}
                className={cn('flex items-center', containerClassName)}
                style={{ gap: `${gap}px` }}
            >
                {leftIcon && (
                    <div data-testid={leftIconTestId || 'text-icon-left'} className="flex-shrink-0">
                        {leftIcon}
                    </div>
                )}

                <div
                    data-testid={textTestId || 'text-icon-text'}
                    className={cn(
                        fontSizeClasses[fontSize],
                        fontWeightClasses[fontWeight],
                        alignClasses[alignText],
                        lineClampClass,
                        textClassName
                    )}
                    style={fontColor ? { color: fontColor } : undefined}
                >
                    {text || children}
                </div>

                {rightIcon && (
                    <div data-testid={rightIconTestId || 'text-icon-right'} className="flex-shrink-0">
                        {rightIcon}
                    </div>
                )}
            </div>
        );
    }
);

TextIcon.displayName = 'TextIcon';
